import React from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import "./UpdateLog.scss";
import { FE_VERSION } from "../../constants/version";

const BE_VERSION = "v2.5.0";

// One entry per release. Older granular per-patch notes were consolidated here so
// the log tracks the version releases rather than every individual change.
const updateLogs = [
  {
    date: "2026-10-10",
    feVersion: "v2.6.0",
    beVersion: "v2.5.0",
    area: "Data sync (admin)",
    description: [
      "New Data sync page in the Admin menu, for admins only: shows whether the award and winner search indexes and the awardee summary table still match the database, and when each was last synced",
      "Check compares each copy with the database and lists what a sync would change: rows to add, rows to remove and rows whose content changed",
      "Sync builds a fresh copy beside the live one and swaps it in, so search and the Awardees tab keep working while it runs; Sync all updates only the copies that are out of date",
    ],
  },
  {
    date: "2026-09-12",
    feVersion: "v2.5.0",
    beVersion: "v2.4.0",
    area: "UTA faculty, chat and typography",
    description: [
      "UTA faculty names now link to their uta.edu profile, opening in a new tab: in the Awardees table, on award pages and in the Awards won by panel",
      "Awardees tab: the UTA button (UTA network only) now filters the list to UTA faculty, combinable with the Academy and Year facets",
      "Chat: Save as Word downloads the conversation as a document in the report's house style, on both the chat page and the assessment page",
      "New typeface pairing: Newsreader for headings, Public Sans for text; the browser tab is titled Scholars",
      "Navigation tabs respond on hover, not only on touch",
    ],
  },
  {
    date: "2026-08-29",
    feVersion: "v2.4.0",
    beVersion: "v2.3.0",
    area: "Assessment reports",
    description: [
      "Reports no longer recommend an award the CV already lists as held. The report says which award was excluded and quotes the line from your CV that shows it",
      "Each award now describes what its past recipients had in common, as counts drawn from the award database, in place of the generic talking points. Recipients are never named",
      "Gaps are written against that group: what most past recipients had that the CV does not yet show, and how to frame it",
      "The report header now takes the candidate's name from the CV itself, so a CV that opens with a sentence instead of a name is no longer mistaken for one",
    ],
  },
  {
    date: "2026-08-25",
    feVersion: "v2.3.0",
    beVersion: "v2.3.0",
    area: "RAG assessment and chat",
    description: [
      "New: upload a CV to get an Award Assessment report, generated by the RAG (Retrieval-Augmented Generation) engine, with a chat panel to ask why an award was or was not recommended",
      "Download the generated report as a Word document",
      "Ask the catalogue questions directly, with no report needed - answers are grounded in real award data, never invented",
    ],
  },
  {
    date: "2026-08-05",
    feVersion: "v2.2.2",
    beVersion: "v2.2.0",
    area: "Naming & readability",
    description: [
      "The site is now called Scholars, in the header and above the Awards search",
      "Organization page: award areas, society facts and award details are shown as bracketed values, [Leadership] [Research Funds], instead of being run together with vertical bars",
    ],
  },
  {
    date: "2026-08-04",
    feVersion: "v2.2.1",
    beVersion: "v2.2.0",
    area: "Academy badges",
    description: [
      "Award pages: National Academy of Medicine members in an award's list of recipients were shown as belonging to no academy at all. All five academy badges now appear there, and the Academies column sorts correctly again",
      "A small number of American Academy of Arts and Sciences members were missing their badge on the same pages for a related reason, and now show it",
    ],
  },
  {
    date: "2026-07-28",
    feVersion: "v2.2.0",
    beVersion: "v2.2.0",
    area: "Awardees & reliability",
    description: [
      "Awardee panel: each award is now expandable, showing the granting organization plus kind, category, level, share of women recipients and the first year on record",
      "Academy logos are showing again for National Academy of Medicine members, and will not silently vanish if the underlying data changes shape",
      "Awardees search now covers everyone: 6,826 people, including the most recent intake, were missing from the search index and are findable again",
      "Landing page statistics no longer blank out when one figure cannot be calculated; the rest are still shown",
      "Awardee counts on the landing page and the Awardees tab now always agree",
    ],
  },
  {
    date: "2026-07-16",
    feVersion: "v2.1.0",
    beVersion: "v2.1.0",
    area: "Search & security",
    description: [
      "Awardees search: separate First name and Last name fields; results default to alphabetical by name",
      "Mobile: tighter table with less wasted space, a single-row pager, and Academy + Year filters inline (no more nested filter taps)",
      "Analyses viewer now loads over an authenticated request in a sandboxed frame, so the access token is no longer put in the URL",
      "API hardening: login brute-force and timing protection, stricter public-data fields, response security headers, and request size + list-size limits",
      "Awardee summary table now rebuilds atomically, so it is never briefly missing during a refresh",
    ],
  },
  {
    date: "2026-07-02",
    feVersion: "v2.0.6",
    beVersion: "v2.0.3",
    area: "Polish & correlated awards",
    description: [
      "Correlated Awards page redesigned (hero, cards, cleaner legend); dropdowns now list all qualifying awards on open; nodes show recipient counts and can no longer mismatch the legend",
      "Award detail: gender chart redesigned with a live male/female fallback when there is no precomputed data; National Academy badges link out; academy legend moved into an info icon; fixed-height scrollable roster",
      "Awards search: 'UTA awards' filter (UTA network) with the Maverick marker, stacking with Honorific; Kind column wraps instead of truncating",
      "Deep-ocean RAISE theme; organization chart never scales below 5 people and states when a society has no data",
    ],
  },
  {
    date: "2026-06-30",
    feVersion: "v2.0.2",
    beVersion: "v2.0.0",
    area: "2.0 redesign",
    description: [
      "Unified Awards / Awardees search shell with shareable URL state (deep-links, refresh and Back preserve the search) and a refresh-safe award detail",
      "Filters: Category, Level, Kind, Organization, plus an Honorific (AAU prestigious) quick-filter",
      "Awardees now cover all 282,544 people with academy, award-count and most-recent-year columns, a page-size selector, and Academy + Year filters",
      "UTA Highlight redesigned: branded hero, responsive faculty card grid, mobile award dropdown",
      "New design system (typography, spacing, heroes, cards, one unified laurel loader) and full mobile responsiveness",
    ],
  },
  {
    date: "2026-06-25",
    feVersion: "v1.8.0",
    beVersion: "v1.6.2",
    area: "Theming",
    description: [
      "Network-aware theming across the whole site: UTA palette for UTA-network visitors, neutral RAISE palette for everyone else",
      "UTA Highlight tab and 'UTA faculty' notes appear only on the UTA network; nav follows the active theme",
    ],
  },
  {
    date: "2026-05-07",
    feVersion: "v1.7.4",
    beVersion: "v1.6.2",
    area: "Graphs & routing",
    description: [
      "Graph engine canonicalizes people by RaiseProjectID (spelling variants count as one) and awards by fk_award; graphs #6-7 rebuilt (Geology, AMChester)",
      "UTA-network-aware landing behind CloudFront: root opens UTA Highlight for configured UTA IP ranges, Organization otherwise",
      "Organization chart derives its year range from the API and hides the rolling and future years",
    ],
  },
  {
    date: "2026-04-28",
    feVersion: "v1.7.1",
    beVersion: "v1.5.9",
    area: "Analyses & graphs",
    description: [
      "FE versioning and a login-gated About version card showing FE and BE deploy dates",
      "Analyses page modernized: hero, sortable table, tag chips, per-graph descriptions, a how-to accordion, and a login-gated hamburger nav",
      "Network graphs #3-6 (physics, geology) with a compound boolean query builder",
      "Member National Academy landing merges the three academy awards into one entry",
    ],
  },
  {
    date: "2026-03-31",
    feVersion: "v1.4.0",
    beVersion: "v1.4.0",
    area: "Auth & Analyses",
    description: [
      "User login system: Login page plus Login/Logout in the nav so team members can sign in for private pages",
      "New login-gated Analyses page for interactive visualizations, served from the backend repo",
      "GitHub Actions deploy workflow with email notifications; ESLint and CI build fixes",
    ],
  },
  {
    date: "2026-03-23",
    feVersion: "v1.3.0",
    beVersion: "v1.3.0",
    area: "Organizations & Awards",
    description: [
      "Society dropdown shows all organizations in a scrollable list with typeahead highlighting",
      "Award detail collapses into an expandable accordion when searching by last name or award name",
      "Fixed last-name search from the award detail page",
    ],
  },
  {
    date: "2026-03-08",
    feVersion: "v1.2.0",
    beVersion: "v1.2.0",
    area: "Awards & Awardees",
    description: [
      "Organization column added; all award and awardee columns support server-side sorting; Enter fires the search",
      "Awardees page uses server-side pagination and a pre-built summary table (about 40x faster)",
      "Backend APIs reorganized into modules (societies, awards, awardees, counts, UTA faculty); frontend uses a relative API URL",
      "Hidden update log added; landing prestigious-award selection fixes",
    ],
  },
  {
    date: "2025-01-01",
    feVersion: "v1.0.0",
    beVersion: "v1.0.0",
    area: "General",
    description: "Initial release of the Academic-Scholars platform.",
  },
];

const UpdateLog = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container maxWidth="md" sx={{ mt: 10, mb: 6 }}>
      <Box textAlign="center" mb={2}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
          Update Log
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 1 }}
        >
          <Chip
            label={`Frontend ${FE_VERSION}`}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Chip
            label={`Backend ${BE_VERSION}`}
            color="secondary"
            variant="outlined"
            size="small"
          />
        </Stack>
      </Box>

      <Card elevation={3} sx={{ borderRadius: "6px" }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow className="update-log-header">
                  <TableCell sx={{ fontWeight: "bold", width: "110px" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "120px" }}>
                    Version
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "120px" }}>
                    Area
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    What Changed
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {updateLogs.map((log, index) => (
                  <TableRow
                    key={index}
                    className="update-log-row"
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {log.date}
                    </TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={0.5}>
                        {log.feVersion && (
                          <Chip
                            label={`FE ${log.feVersion}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                        {log.beVersion && (
                          <Chip
                            label={`BE ${log.beVersion}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                        {!log.feVersion && !log.beVersion && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.75rem"
                          >
                            -
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.area}
                        size="small"
                        sx={{ fontSize: "0.75rem", height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      {Array.isArray(log.description) ? (
                        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                          {log.description.map((point, i) => (
                            <Typography
                              key={i}
                              component="li"
                              variant="body2"
                              sx={{ mb: 0.25 }}
                            >
                              {point}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          {log.description}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UpdateLog;
